import { useMemo, useState } from 'react'
import { useForge } from '../data/useForge'
import { useI18n } from '../i18n/useI18n'
import { Icon } from '../components/Icon'
import { Layout } from '../components/Layout'
import type { SkillCategory } from '../types'

const CATEGORY_ORDER: SkillCategory[] = ['workflow', 'writing', 'research', 'figures', 'compute']

export default function Catalog() {
  const { t } = useI18n()
  const { registry, selectedSkills, toggleSkill } = useForge()
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<SkillCategory | 'all'>('all')

  const filtered = useMemo(() => {
    const skills = registry?.skills ?? []
    return skills.filter((skill) => {
      const matchesCategory = activeCategory === 'all' || skill.category === activeCategory
      const q = query.trim().toLowerCase()
      const matchesQuery =
        q === '' ||
        skill.name.toLowerCase().includes(q) ||
        skill.nameZh.toLowerCase().includes(q) ||
        skill.description.toLowerCase().includes(q) ||
        skill.tags.some((tag) => tag.toLowerCase().includes(q))
      return matchesCategory && matchesQuery
    })
  }, [registry, activeCategory, query])

  return (
    <Layout>
      <div className="px-4 py-12 md:px-margin-desktop">
        <header className="mb-10">
          <h1 className="font-display-lg text-display-lg text-on-surface">{t.catalog.title}</h1>
          <p className="mt-4 max-w-2xl font-body-lg text-body-lg text-on-surface-variant">{t.catalog.subtitle}</p>

          <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center">
            <div className="forge-input-focus flex w-full max-w-md items-center rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-3 transition-all duration-200">
              <Icon name="search" size={20} className="mr-3 text-on-surface-variant" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.catalog.searchPlaceholder}
                className="w-full bg-transparent font-body-md text-body-md text-on-surface outline-none placeholder:text-on-surface-variant/50"
              />
            </div>
          </div>

          {/* Category tabs */}
          <div className="mt-8 flex gap-6 overflow-x-auto border-b border-outline-variant pb-2">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className={`whitespace-nowrap pb-2 font-label-sm text-label-sm transition-colors ${
                activeCategory === 'all'
                  ? 'border-b-2 border-primary font-bold text-primary'
                  : 'text-on-surface-variant hover:text-primary'
              }`}
            >
              All
            </button>
            {CATEGORY_ORDER.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap pb-2 font-label-sm text-label-sm transition-colors ${
                  activeCategory === cat
                    ? 'border-b-2 border-primary font-bold text-primary'
                    : 'text-on-surface-variant hover:text-primary'
                }`}
              >
                {t.catalog.categories[cat]}
              </button>
            ))}
          </div>
        </header>

        {/* Skill grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {filtered.map((skill) => {
              const selected = selectedSkills.includes(skill.id)
              return (
                <div
                  key={skill.id}
                  className={`tactile-card flex flex-col rounded-lg border p-card-padding ${
                    selected
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-outline-variant'
                  } bg-surface-container-lowest`}
                >
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="border-b border-surface-container-high pb-2 font-headline-md text-headline-md text-on-surface">
                        {skill.name}
                      </h3>
                      <p className="mt-3 font-body-md text-body-md text-on-surface-variant">{skill.description}</p>
                    </div>
                    <span className="ml-4 rounded-full bg-primary-container/20 p-2 text-primary">
                      <Icon name={skill.icon} size={22} />
                    </span>
                  </div>

                  <div className="mb-6 flex flex-wrap gap-2">
                    {skill.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded border border-outline-variant bg-surface-container px-2 py-1 font-label-sm text-label-sm text-on-surface-variant"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto flex items-center justify-between border-t border-surface-container-high pt-4">
                    <a
                      href={skill.sourceHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 font-label-sm text-label-sm text-secondary transition-colors hover:underline"
                    >
                      <Icon name="link" size={16} />
                      {t.catalog.sourceRepo}
                    </a>
                    <button
                      type="button"
                      onClick={() => toggleSkill(skill.id)}
                      className={`flex items-center gap-2 rounded px-4 py-2 font-label-sm text-label-sm transition-colors ${
                        selected
                          ? 'bg-primary text-on-primary'
                          : 'border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                      }`}
                    >
                      <Icon name={selected ? 'check' : 'add'} size={18} />
                      {selected ? t.catalog.added : t.catalog.addToCommand}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-outline-variant p-12 text-center">
            <Icon name="search" size={32} className="mx-auto mb-3 text-outline" />
            <p className="font-body-lg text-body-lg text-on-surface-variant">{t.catalog.noResults}</p>
          </div>
        )}
      </div>
    </Layout>
  )
}
